import React, { useState, useEffect } from "react";
import { PageStructure } from "../../pages";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Avatar,
  Chip,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Checkbox,
  TextField,
  FormControlLabel,
  CircularProgress,
  Alert,
  Button,
  TablePagination,
  TableSortLabel,
} from "@mui/material";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import PreviewIcon from "@mui/icons-material/Preview";
import AddIcon from "@mui/icons-material/Add";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import Select from "react-select";
import {
  Loader,
  AddChargeButton,
  PopUpWindow,
  ChargeForm,
  InvoiceForm,
  InvoiceTable,
  TransactionTable,
  PrimaryButton,
  NewFundTransactionForm,
  GiveTransactionButton,
} from "../../components";
import ChargeDeleteDialog from "../../components/chargeDeleteDialog/ChargeDeleteDialog";
import ChargeModelPreview from "../../components/chargeModelPreview/ChargeModelPreview";
import apiUrl from "../../components/api";
import axios from "axios";
import io from "socket.io-client";

// Utility function to generate a color for an avatar based on name
const getColor = (name) => {
  if (!name) return "#cccccc";
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  const color = `hsl(${hash % 360}, 100%, 30%)`;
  return color;
};

const formatCurrency = (amount) => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "EUR",
  }).format(amount);
};

// TabPanel component to handle tab content
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`property-tabpanel-${index}`}
      aria-labelledby={`property-tab-${index}`}
      {...other}
      style={{ width: "100%" }}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

// Inner TabPanel component for financial data tabs
function FinanceTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`finance-tabpanel-${index}`}
      aria-labelledby={`finance-tab-${index}`}
      {...other}
      style={{ width: "100%" }}
    >
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  );
}

// Function to get aria-controls property for tabs
function a11yProps(index) {
  return {
    id: `property-tab-${index}`,
    "aria-controls": `property-tabpanel-${index}`,
  };
}

// Function to get aria-controls property for inner finance tabs
function financeTabProps(index) {
  return {
    id: `finance-tab-${index}`,
    "aria-controls": `finance-tabpanel-${index}`,
  };
}

const PreviewOwner = () => {
  const [user, setUser] = useState(JSON.parse(localStorage.getItem("user")));
  const [loading, setLoading] = useState(true);
  const [ownersLoading, setOwnersLoading] = useState(true);
  const [owners, setOwners] = useState([]);
  const [selectedOwner, setSelectedOwner] = useState(null);
  const [ownerDetails, setOwnerDetails] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [financeTabValues, setFinanceTabValues] = useState({});
  const [allPropertiesTabValue, setAllPropertiesTabValue] = useState(0);
  const [selectedPropertyForCharge, setSelectedPropertyForCharge] =
    useState(null);
  const [propertyCharges, setPropertyCharges] = useState({}); // Store charges for each property
  const [chargesLoading, setChargesLoading] = useState({}); // Track loading state for each property's charges
  // New state variables for viewing charge details
  const [selectedCharge, setSelectedCharge] = useState(null);
  const [chargeViewOpen, setChargeViewOpen] = useState(false);
  // New state variables for invoice form popup
  const [invoiceFormOpen, setInvoiceFormOpen] = useState(false);
  const [selectedChargeForInvoice, setSelectedChargeForInvoice] =
    useState(null);
  // Add state for multiple charge selection
  const [selectedCharges, setSelectedCharges] = useState([]);

  // Add state for payment dialog
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [selectedChargeForPayment, setSelectedChargeForPayment] =
    useState(null);

  // Pagination and sorting state for charges
  const [chargesPagination, setChargesPagination] = useState({});
  const [chargesSorting, setChargesSorting] = useState({});
  const [chargesTotalItems, setChargesTotalItems] = useState({});

  // Pagination and sorting state for "All Properties" charges tab
  const [allPropertiesChargesPagination, setAllPropertiesChargesPagination] =
    useState({ page: 0, rowsPerPage: 10 });
  const [allPropertiesChargesSorting, setAllPropertiesChargesSorting] =
    useState({ sortField: "created_at", sortOrder: -1 });

  // States for aggregated owner charges (fetched from backend)
  const [ownerCharges, setOwnerCharges] = useState([]);
  const [ownerChargesLoading, setOwnerChargesLoading] = useState(false);
  const [ownerChargesTotalItems, setOwnerChargesTotalItems] = useState(0);

  const [paymentError, setPaymentError] = useState("");
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);

  // Add state for transaction-based payment allocation
  const [transactionAllocations, setTransactionAllocations] = useState([]);
  const [totalAllocatedAmount, setTotalAllocatedAmount] = useState(0);

  // Add state for new ChargeDeleteDialog
  const [chargeDeleteDialogOpen, setChargeDeleteDialogOpen] = useState(false);
  const [chargeToDelete, setChargeToDelete] = useState(null);

  // Add state for charge model preview
  const [modelPreviewOpen, setModelPreviewOpen] = useState(false);
  const [selectedChargeForPreview, setSelectedChargeForPreview] =
    useState(null);

  const [error, setError] = useState("");
  const [fundTransactionPopupOpen, setFundTransactionPopupOpen] =
    useState(false);

  // Add state for edit balance dialog
  const [editBalanceDialogOpen, setEditBalanceDialogOpen] = useState(false);
  const [newBalance, setNewBalance] = useState(0);
  const [balanceLoading, setBalanceLoading] = useState(false);

  // Add socket connection
  useEffect(() => {
    const socket = io(apiUrl);

    // Listen for charge updates
    socket.on("charge", (data) => {
      console.log("Socket received - charge:", data);
      if (data.action === "create" || data.action === "update") {
        const charge = data.charge;
        if (charge.property) {
          const propertyId =
            typeof charge.property === "object"
              ? charge.property._id
              : charge.property;
          setPropertyCharges((prev) => {
            const currentCharges = prev[propertyId] || [];
            if (data.action === "create") {
              return {
                ...prev,
                [propertyId]: [...currentCharges, charge],
              };
            } else {
              return {
                ...prev,
                [propertyId]: currentCharges.map((c) =>
                  c._id === charge._id ? charge : c
                ),
              };
            }
          });

          // Also update owner charges immediately
          if (data.action === "create") {
            // Check if this charge belongs to the selected owner's properties
            if (
              selectedOwner &&
              ownerDetails?.properties?.some(
                (p) => (typeof p === "object" ? p._id : p) === propertyId
              )
            ) {
              setOwnerCharges((prev) => [charge, ...prev]);
              setOwnerChargesTotalItems((prev) => prev + 1);
            }
          } else if (data.action === "update") {
            // Update the charge in owner charges if it exists
            setOwnerCharges((prev) =>
              prev.map((c) => (c._id === charge._id ? charge : c))
            );
          }

          // Refresh owner charges when a charge is created or updated (for data consistency)
          if (selectedOwner) {
            fetchOwnerCharges();
          }
        }
      } else if (data.action === "delete") {
        const chargeId = data.chargeId;
        console.log("Socket received - charge delete:", chargeId);

        setPropertyCharges((prev) => {
          const newCharges = {};
          Object.keys(prev).forEach((propertyId) => {
            newCharges[propertyId] = prev[propertyId].filter(
              (c) => c._id !== chargeId
            );
          });
          return newCharges;
        });

        // Also update owner charges immediately to reflect the deletion
        setOwnerCharges((prev) => prev.filter((c) => c._id !== chargeId));

        // Update total items count as well
        setOwnerChargesTotalItems((prev) => Math.max(0, prev - 1));

        // Refresh owner charges when a charge is deleted (for data consistency)
        if (selectedOwner) {
          console.log(
            "Refreshing owner charges after deletion...",
            selectedOwner
          );
          fetchOwnerCharges();
        } else {
          console.log("No selected owner, skipping owner charges refresh");
        }
      }
    });

    // Listen for transaction events and refresh when relevant to selected owner
    socket.on("newTransaction", (tx) => {
      try {
        const selectedOwnerId =
          (selectedOwner && selectedOwner.value) || selectedOwner || null;
        if (!selectedOwnerId) return;

        const involvedIds = [
          tx?.user?._id,
          tx?.receiver?._id,
          tx?.sender?._id,
          tx?.approved_by?._id,
        ].filter(Boolean);

        if (involvedIds.includes(selectedOwnerId)) {
          // Refresh owner details (balance, remaining_transactions, etc.)
          fetchOwnerDetails(selectedOwnerId);

          // Refresh owner aggregated charges
          fetchOwnerCharges();

          // If a property in current view is affected, refresh its charges
          const propertyId = tx?.property?._id || tx?.property;
          if (propertyId && propertyCharges[propertyId]) {
            fetchPropertyCharges(propertyId);
          }
        }
      } catch (e) {
        console.warn("Failed handling newTransaction socket:", e);
      }
    });

    socket.on("updateTransaction", (tx) => {
      try {
        const selectedOwnerId =
          (selectedOwner && selectedOwner.value) || selectedOwner || null;
        if (!selectedOwnerId) return;

        const involvedIds = [
          tx?.user?._id,
          tx?.receiver?._id,
          tx?.sender?._id,
          tx?.approved_by?._id,
        ].filter(Boolean);

        if (involvedIds.includes(selectedOwnerId)) {
          fetchOwnerDetails(selectedOwnerId);
          fetchOwnerCharges();
          const propertyId = tx?.property?._id || tx?.property;
          if (propertyId && propertyCharges[propertyId]) {
            fetchPropertyCharges(propertyId);
          }
        }
      } catch (e) {
        console.warn("Failed handling updateTransaction socket:", e);
      }
    });

    // Direct balance updates emitted as a separate event by backend
    socket.on("updateUserBalance", (payload) => {
      try {
        const selectedOwnerId =
          (selectedOwner && selectedOwner.value) || selectedOwner || null;
        if (!selectedOwnerId) return;

        if (payload?.user_id === selectedOwnerId) {
          // Update owners list entry
          setOwners((prevOwners) =>
            prevOwners.map((owner) =>
              owner._id === payload.user_id
                ? { ...owner, balance: parseFloat(payload.new_balance) || 0 }
                : owner
            )
          );

          // Update selected owner's details balance label
          setOwnerDetails((prev) =>
            prev
              ? { ...prev, balance: parseFloat(payload.new_balance) || 0 }
              : prev
          );
        }
      } catch (e) {
        console.warn("Failed handling updateUserBalance socket:", e);
      }
    });

    // Listen for owner balance and remaining transactions updates
    socket.on("user", (data) => {
      console.log("Socket received - user:", data);
      if (data.action === "updateBalance" && data.user) {
        // Update the owners array to reflect balance changes in the Select component
        setOwners((prevOwners) =>
          prevOwners.map((owner) =>
            owner._id === data.user._id
              ? { ...owner, balance: parseFloat(data.user.balance) || 0 }
              : owner
          )
        );

        // Update owner details if this is the currently selected owner
        if (selectedOwner.value === data.user._id) {
          setOwnerDetails((prev) => ({
            ...prev,
            balance: (parseFloat(data.user.balance) || 0).toFixed(2), // Ensure balance is formatted as a string with 2 decimal places
          }));

          setSelectedOwner({
            label: `€${(parseFloat(data.user.balance) || 0).toFixed(2)} - ${
              ownerDetails.name
            } - ${ownerDetails.tax_id || "No Tax ID"} - ${
              ownerDetails.companies.length > 0
                ? ownerDetails.companies
                    .map((company) => company.taxId)
                    .join(", ")
                : "No Companies"
            } - ${ownerDetails.properties.map((property) => property.name)}`,
            value: data.user._id,
          });
        }
      }

      if (
        data.action === "updateRemainingTransactions" &&
        data.user &&
        selectedOwner === data.user._id
      ) {
        setOwnerDetails((prev) => ({
          ...prev,
          remaining_transactions: data.user.remaining_transactions,
        }));
      }
    });

    // Listen for contract updates that affect charges
    socket.on("contract", (data) => {
      console.log("Socket received - contract:", data);
      if (
        data.action === "create" ||
        data.action === "update" ||
        data.action === "delete"
      ) {
        // Refresh owner details to reflect any changes in charges
        if (selectedOwner) {
          fetchOwnerDetails(selectedOwner);
        }
      }
    });

    // Listen for action updates that affect charges
    socket.on("actionCreated", (action) => {
      console.log("Socket received - actionCreated:", action);
      if (action.property) {
        const propertyId =
          typeof action.property === "object"
            ? action.property._id
            : action.property;
        if (propertyCharges[propertyId]) {
          fetchPropertyCharges(propertyId);
        }
      }
    });

    socket.on("actionUpdated", (action) => {
      console.log("Socket received - actionUpdated:", action);
      if (action.property) {
        const propertyId =
          typeof action.property === "object"
            ? action.property._id
            : action.property;
        if (propertyCharges[propertyId]) {
          fetchPropertyCharges(propertyId);
        }
      }
    });

    socket.on("actionDeleted", (actionId) => {
      console.log("Socket received - actionDeleted:", actionId);
      // Refresh all property charges to ensure consistency
      if (ownerDetails?.properties) {
        ownerDetails.properties.forEach((property) => {
          if (propertyCharges[property._id]) {
            fetchPropertyCharges(property._id);
          }
        });
      }
    });

    // Listen for pool action updates
    socket.on("poolAction", (data) => {
      console.log("Socket received - poolAction:", data);
      if (
        data.action === "create" ||
        data.action === "update" ||
        data.action === "delete"
      ) {
        const propertyId =
          data.poolAction?.property?._id || data.poolAction?.property;
        if (propertyId && propertyCharges[propertyId]) {
          fetchPropertyCharges(propertyId);
        }
      }
    });

    // Listen for invoice updates that affect charges
    socket.on("invoice", (data) => {
      console.log("Socket received - invoice:", data);
      if (
        data.action === "create" ||
        data.action === "update" ||
        data.action === "delete"
      ) {
        const invoice = data.invoice;
        if (invoice && invoice.property) {
          const propertyId =
            typeof invoice.property === "object"
              ? invoice.property._id
              : invoice.property;

          // Refresh charges for the property affected by the invoice
          if (propertyCharges[propertyId]) {
            fetchPropertyCharges(propertyId);
          }
        }
      }
    });

    // Cleanup socket listeners on component unmount
    return () => {
      socket.off("charge");
      socket.off("user");
      socket.off("contract");
      socket.off("actionCreated");
      socket.off("actionUpdated");
      socket.off("actionDeleted");
      socket.off("poolAction");
      socket.off("invoice");
  socket.off("newTransaction");
  socket.off("updateTransaction");
  socket.off("updateUserBalance");
      socket.disconnect();
    };
  }, [selectedOwner, ownerDetails?.properties, propertyCharges]);

  // Handler for viewing charge details
  const handleViewCharge = (charge) => {
    setSelectedCharge(charge);
    setChargeViewOpen(true);
  };

  // Handler for closing charge view popup
  const handleCloseChargeView = () => {
    setChargeViewOpen(false);
    setSelectedCharge(null);
  };

  // Handler for creating invoice from charge
  const handleCreateInvoice = (charge) => {
    setSelectedChargeForInvoice(charge);
    setInvoiceFormOpen(true);
  };

  // Handler for closing invoice form popup
  const handleCloseInvoiceForm = () => {
    setInvoiceFormOpen(false);
    setSelectedChargeForInvoice(null);
    setSelectedCharges([]);
  };

  // Handler for successful invoice creation
  const handleInvoiceCreated = (newInvoice) => {
    handleCloseInvoiceForm();

    // Refresh charges for this property
    if (selectedChargeForInvoice && selectedChargeForInvoice.property) {
      const propertyId =
        typeof selectedChargeForInvoice.property === "object"
          ? selectedChargeForInvoice.property._id
          : selectedChargeForInvoice.property;

      fetchPropertyCharges(propertyId);
    }

    // Also refresh all owner details to reflect changes
    fetchOwnerDetails(selectedOwner);
  };

  // Handler for deleting a charge
  const handleDeleteCharge = (charge) => {
    setChargeToDelete(charge);
    setChargeDeleteDialogOpen(true);
  };

  // Handler for successful charge deletion
  const handleDeleteSuccess = (deletedCharge) => {
    // Refresh charges for the property
    // const propertyId =
    //   typeof deletedCharge.property === "object"
    //     ? deletedCharge.property._id
    //     : deletedCharge.property;

    // if (propertyCharges[propertyId]) {
    //   // fetchPropertyCharges(propertyId);
    // }

    // Also refresh all owner details to reflect changes
    // fetchOwnerDetails(selectedOwner);

    // Clear any previous errors
    setError("");
  };

  // Handler for closing charge delete dialog
  const handleCloseChargeDeleteDialog = () => {
    setChargeDeleteDialogOpen(false);
    setChargeToDelete(null);
  };

  // Handler for opening model preview
  const handleOpenModelPreview = (charge) => {
    setSelectedChargeForPreview(charge);
    setModelPreviewOpen(true);
  };

  // Handler for closing model preview
  const handleCloseModelPreview = () => {
    setModelPreviewOpen(false);
    setSelectedChargeForPreview(null);
  };

  // Add handlers for multiple charge selection
  const handleChargeSelect = (charge, isSelected) => {
    if (isSelected) {
      setSelectedCharges([...selectedCharges, charge]);
    } else {
      setSelectedCharges(selectedCharges.filter((c) => c._id !== charge._id));
    }
  };

  const handleCreateInvoiceFromMultipleCharges = () => {
    // No longer need to check if all charges have the same property
    // since we now support multiple properties in a single invoice
    
    // Get all unique properties from selected charges
    const uniqueProperties = [];
    const propertyIds = new Set();
    
    selectedCharges.forEach((charge) => {
      const propertyId =
        typeof charge.property === "object"
          ? charge.property._id
          : charge.property;
      
      if (!propertyIds.has(propertyId)) {
        propertyIds.add(propertyId);
        uniqueProperties.push({
          _id: propertyId,
          name: typeof charge.property === "object" 
            ? charge.property.name 
            : `Property ${propertyId}`
        });
      }
    });

    // Create an invoice with multiple charges and potentially multiple properties
    setSelectedChargeForInvoice({
      ...selectedCharges[0],
      multipleCharges: selectedCharges,
      multipleProperties: uniqueProperties.length > 1 ? uniqueProperties : null,
    });
    setInvoiceFormOpen(true);
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    // When changing tabs, update the selected property for charge
    if (newValue === 0) {
      // All properties tab
      setSelectedPropertyForCharge(null);
    } else if (
      ownerDetails?.properties &&
      ownerDetails.properties[newValue - 1]
    ) {
      // Individual property tab
      const property = ownerDetails.properties[newValue - 1];
      setSelectedPropertyForCharge(property);

      // Fetch charges for the selected property if we haven't already
      if (!propertyCharges[property._id]) {
        fetchPropertyCharges(property._id);
      }
    }
  };

  // Handle finance tab change for a specific property
  const handleFinanceTabChange = (propertyId, newValue) => {
    setFinanceTabValues((prev) => ({
      ...prev,
      [propertyId]: newValue,
    }));

    // If changing to charges tab (index 0) and charges haven't been fetched yet, fetch them
    if (newValue === 0 && !propertyCharges[propertyId]) {
      fetchPropertyCharges(propertyId);
    }
  };

  // Handle tab change for all properties finance tabs
  const handleAllPropertiesTabChange = (event, newValue) => {
    setAllPropertiesTabValue(newValue);

    // If changing to charges tab (index 0), ensure we've fetched charges for all properties
    if (newValue === 0 && ownerDetails?.properties) {
      ownerDetails.properties.forEach((property) => {
        if (!propertyCharges[property._id]) {
          fetchPropertyCharges(property._id);
        }
      });
    }
  };

  // Fetch charges for a specific property
  const fetchPropertyCharges = async (propertyId) => {
    try {
      // Set loading state for this property's charges
      setChargesLoading((prev) => ({ ...prev, [propertyId]: true }));

      // Get pagination and sorting for this property
      const pagination = chargesPagination[propertyId] || {
        page: 0,
        rowsPerPage: 10,
      };
      const sorting = chargesSorting[propertyId] || {
        sortField: "paid",
        sortOrder: 1,
      };

      const response = await axios.get(
        `${apiUrl}/charge/property/${propertyId}`,
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
          params: {
            page: pagination.page + 1, // Backend expects 1-based page numbers
            perPage: pagination.rowsPerPage,
            sortField: sorting.sortField,
            sortOrder: sorting.sortOrder,
          },
        }
      );

      // Store the charges for this property
      setPropertyCharges((prev) => ({
        ...prev,
        [propertyId]: response.data.charges,
      }));

      // Store total items for pagination
      setChargesTotalItems((prev) => ({
        ...prev,
        [propertyId]: response.data.totalItems,
      }));

      // Update loading state
      setChargesLoading((prev) => ({ ...prev, [propertyId]: false }));
    } catch (err) {
      console.error(`Failed to fetch charges for property ${propertyId}:`, err);
      setError(
        `Failed to fetch charges for property. ${
          err.response?.data?.message || err.message
        }`
      );
      setChargesLoading((prev) => ({ ...prev, [propertyId]: false }));
    }
  };

  // Fetch all charges for owner's properties with pagination (using new backend endpoint)
  const fetchOwnerCharges = async () => {
    if (!selectedOwner) return;

    try {
      setOwnerChargesLoading(true);

      const { page, rowsPerPage } = allPropertiesChargesPagination;
      const { sortField, sortOrder } = allPropertiesChargesSorting;

      // Ensure we get the owner ID correctly
      const ownerId =
        typeof selectedOwner === "object" ? selectedOwner._id : selectedOwner;

      console.log("Fetching owner charges for owner ID:", ownerId);

      const response = await axios.get(`${apiUrl}/charge/owner/${selectedOwner.value}`, {
        headers: {
          Authorization: `Bearer ${user.token}`,
        },
        params: {
          page: page + 1, // Backend expects 1-based page numbers
          perPage: rowsPerPage,
          sortField: sortField,
          sortOrder: sortOrder,
        },
      });

      setOwnerCharges(response.data.charges);
      setOwnerChargesTotalItems(response.data.totalItems);
      setOwnerChargesLoading(false);
    } catch (err) {
      console.error(`Failed to fetch owner charges:`, err);
      setError(
        `Failed to fetch owner charges. ${
          err.response?.data?.message || err.message
        }`
      );
      setOwnerChargesLoading(false);
    }
  };

  // Handle sorting change for charges table
  const handleChargesSortChange = (propertyId, sortField) => {
    const currentSorting = chargesSorting[propertyId] || {
      sortField: "paid",
      sortOrder: 1,
    };

    let newSortOrder = 1; // Default ascending
    if (currentSorting.sortField === sortField) {
      // If clicking the same field, toggle sort order
      newSortOrder = currentSorting.sortOrder === 1 ? -1 : 1;
    }

    const newSorting = {
      sortField: sortField,
      sortOrder: newSortOrder,
    };

    // Update sorting state for this property
    setChargesSorting((prev) => ({
      ...prev,
      [propertyId]: newSorting,
    }));

    // Reset to first page when sorting changes
    setChargesPagination((prev) => ({
      ...prev,
      [propertyId]: { ...prev[propertyId], page: 0 },
    }));

    // Fetch charges with new sorting
    fetchPropertyCharges(propertyId);
  };

  // Handle page change for charges table
  const handleChargesPageChange = (propertyId, newPage) => {
    setChargesPagination((prev) => ({
      ...prev,
      [propertyId]: { ...prev[propertyId], page: newPage },
    }));

    // Fetch charges for new page
    fetchPropertyCharges(propertyId);
  };

  // Handle rows per page change for charges table
  const handleChargesRowsPerPageChange = (propertyId, event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);

    setChargesPagination((prev) => ({
      ...prev,
      [propertyId]: {
        ...prev[propertyId],
        rowsPerPage: newRowsPerPage,
        page: 0, // Reset to first page when changing rows per page
      },
    }));

    // Fetch charges with new page size
    fetchPropertyCharges(propertyId);
  };

  // Handle sorting change for "All Properties" charges tab
  const handleAllPropertiesChargesSortChange = (sortField) => {
    const currentSorting = allPropertiesChargesSorting;

    let newSortOrder = -1; // Default descending
    if (currentSorting.sortField === sortField) {
      // If clicking the same field, toggle sort order
      newSortOrder = currentSorting.sortOrder === 1 ? -1 : 1;
    }

    const newSorting = {
      sortField: sortField,
      sortOrder: newSortOrder,
    };

    // Update sorting state
    setAllPropertiesChargesSorting(newSorting);

    // Reset to first page when sorting changes
    setAllPropertiesChargesPagination((prev) => ({
      ...prev,
      page: 0,
    }));

    // Fetch charges with new sorting
    fetchOwnerCharges();
  };

  // Handle page change for "All Properties" charges tab
  const handleAllPropertiesChargesPageChange = (event, newPage) => {
    setAllPropertiesChargesPagination((prev) => ({
      ...prev,
      page: newPage,
    }));

    // Fetch charges for new page
    fetchOwnerCharges();
  };

  // Handle rows per page change for "All Properties" charges tab
  const handleAllPropertiesChargesRowsPerPageChange = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);

    setAllPropertiesChargesPagination({
      rowsPerPage: newRowsPerPage,
      page: 0, // Reset to first page when changing rows per page
    });

    // Fetch charges with new page size
    fetchOwnerCharges();
  };

  // Get aggregated and sorted charges for "All Properties" tab
  const getAllPropertiesCharges = () => {
    // Return the charges fetched from the backend with pagination already applied
    return {
      charges: ownerCharges,
      totalCharges: ownerChargesTotalItems,
    };
  };

  // Fetch the list of owners (users with role "owner") for the dropdown
  useEffect(() => {
    const fetchOwners = async () => {
      try {
        setOwnersLoading(true);
        const response = await axios.get(`${apiUrl}/auth/owners`, {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        });
        setOwners(response.data.owners);
        setOwnersLoading(false);
      } catch (error) {
        setError("Failed to fetch owners. Please try again.");
        setOwnersLoading(false);
      }
    };
    fetchOwners();
  }, []);

  const handleChargeAdded = (newCharge) => {
    // Refresh charges for this property
    if (newCharge.property) {
      fetchPropertyCharges(newCharge.property);
    }

    // Also refresh all owner details to reflect changes
    fetchOwnerDetails(selectedOwner);
  };

  // Fetch details of the selected owner
  const fetchOwnerDetails = async (ownerId) => {
    if (!ownerId) {
      setLoading(false);
      setOwnerDetails(null);
      return;
    }

    try {
      setLoading(true);
      const response = await axios.get(`${apiUrl}/auth/owner/${ownerId}`, {
        headers: {
          Authorization: `Bearer ${user.token}`,
        },
      });
      setOwnerDetails(response.data.owner);

      // Initialize finance tabs for each property
      if (response.data.owner.properties) {
        const initialFinanceTabValues = {};
        response.data.owner.properties.forEach((property) => {
          initialFinanceTabValues[property._id] = 0;

          // Fetch charges for each property
          fetchPropertyCharges(property._id);
        });
        setFinanceTabValues(initialFinanceTabValues);

        // Set the selected property if we're on a property tab
        if (tabValue > 0 && tabValue <= response.data.owner.properties.length) {
          setSelectedPropertyForCharge(
            response.data.owner.properties[tabValue - 1]
          );
        } else {
          setSelectedPropertyForCharge(null);
        }
      }

      setLoading(false);
    } catch (err) {
      setError("Failed to fetch owner details. Please try again.");
      setLoading(false);
    }
  };

  // Fetch owner charges when pagination/sorting changes or owner is selected
  useEffect(() => {
    if (selectedOwner) {
      fetchOwnerCharges();
    }
  }, [
    selectedOwner,
    allPropertiesChargesPagination,
    allPropertiesChargesSorting,
  ]);

  // useEffect(() => {
  //   fetchOwnerDetails(selectedOwner);

  //   // Clear property charges when owner changes
  //   setPropertyCharges({});
  //   setChargesLoading({});
  // }, [selectedOwner]);

  const handleOwnerSelect = async (selectedOption) => {

    if (!selectedOption || !selectedOption.value) {
      setSelectedOwner(null);
      setOwnerDetails(null);
      return;
    }
    setSelectedOwner({
      value: selectedOption.value,
      label: selectedOption.label,
    });
    console.log("Selected Owner ID:", selectedOption);
    // Reset tabs when changing owner
    setTabValue(0);
    setAllPropertiesTabValue(0);
    setFinanceTabValues({});
    setSelectedPropertyForCharge(null);

    await fetchOwnerDetails(selectedOption.value);
    setPropertyCharges({});
    setChargesLoading({});
  };

  // Payment validation function
  const validatePayment = () => {
    // Reset any previous errors
    setPaymentError("");

    // Check if payment amount is valid
    if (!totalAllocatedAmount || totalAllocatedAmount <= 0) {
      const errorMessage =
        transactionAllocations.length > 0
          ? "Please allocate payment amounts to at least one transaction."
          : "Payment amount must be greater than zero.";
      setPaymentError(errorMessage);
      return false;
    }

    // Check if payment amount exceeds remaining charge amount
    if (totalAllocatedAmount > selectedChargeForPayment.remaining_amount) {
      setPaymentError(
        "Total payment amount cannot exceed the remaining charge amount."
      );
      return false;
    }

    // If we have transaction allocations, validate them
    if (transactionAllocations.length > 0) {
      const allocatedTransactions = transactionAllocations.filter(
        (transaction) => transaction.allocatedAmount > 0
      );

      if (allocatedTransactions.length === 0) {
        setPaymentError(
          "Please allocate payment amounts to at least one transaction."
        );
        return false;
      }
    }

    return true;
  };

  // Payment submission function
  const submitPayment = async () => {
    setProcessingPayment(true);

    try {
      // Prepare payment data
      let paymentData = { amount: totalAllocatedAmount };

      // If we have transaction allocations, include them
      if (transactionAllocations.length > 0) {
        const allocatedTransactions = transactionAllocations
          .filter((transaction) => transaction.allocatedAmount > 0)
          .map((transaction) => ({
            transactionId: transaction.transactionId,
            amount: transaction.allocatedAmount,
          }));

        paymentData.transactionAllocations = allocatedTransactions;
      }

      // Submit payment to backend
      await axios.put(
        `${apiUrl}/charge/pay/${selectedChargeForPayment._id}`,
        paymentData,
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
            "Content-Type": "application/json",
          },
        }
      );
      console.log("Payment Data:", paymentData);
      setPaymentSuccess(true);
    } catch (error) {
      setPaymentError(
        error.response?.data?.message ||
          "Failed to process payment. Please try again."
      );
    } finally {
      setProcessingPayment(false);
    }
  };

  // Combined payment handler
  const handlePaymentSubmission = async () => {
    // First validate the payment
    if (!validatePayment()) {
      return;
    }

    // If validation passes, submit the payment
    await submitPayment();
  };

  // Handler for opening edit balance dialog
  const handleEditBalance = () => {
    setNewBalance(parseFloat(ownerDetails.balance) || 0);
    setEditBalanceDialogOpen(true);
  };

  // Handler for closing edit balance dialog
  const handleCloseEditBalance = () => {
    setEditBalanceDialogOpen(false);
    setNewBalance(0);
    setError("");
  };

  // Handler for updating balance
  const handleUpdateBalance = async () => {
    setBalanceLoading(true);
    setError("");

    try {
      const response = await axios.put(
        `${apiUrl}/auth/owner/${ownerDetails._id}/balance`,
        { balance: parseFloat(newBalance) },
        {
          headers: {
            Authorization: "Bearer " + user.token,
          },
        }
      );

      if (response.data) {
        // Update the owner details with the new balance
        setOwnerDetails((prev) => ({
          ...prev,
          balance: parseFloat(newBalance),
        }));

        // Close the dialog
        handleCloseEditBalance();

        // You could add a success message here if needed
      }
    } catch (error) {
      console.error("Error updating balance:", error);
      setError(
        error.response?.data?.message ||
          "Failed to update balance. Please try again."
      );
    } finally {
      setBalanceLoading(false);
    }
  };

  return (
    <PageStructure>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Preview Owner
        </Typography>

        <Box sx={{ maxWidth: 500, mb: 4 }}>
          {ownersLoading ? (
            <Loader />
          ) : (
            <Select
              options={owners.map((owner) => ({
                value: owner._id,
                label: `€${(parseFloat(owner.balance) || 0).toFixed(2)} -${
                  owner.name
                } - ${owner.tax_id || "No Tax ID"} - ${
                  owner.companies.length > 0
                    ? owner.companies.map((company) => company.taxId).join(", ")
                    : "No Companies"
                } - ${owner.properties.map((property) => property.name)}`,
              }))}
              value={
                selectedOwner
                  ? {
                      value: selectedOwner,
                      label: `€${(
                        parseFloat(ownerDetails?.balance) || 0
                      ).toFixed(2)} - ${
                        ownerDetails?.name || "Select an owner"
                      } - ${ownerDetails?.tax_id || "No Tax ID"} - ${
                        ownerDetails?.companies?.length > 0
                          ? ownerDetails.companies
                              .map((company) => company.taxId)
                              .join(", ")
                          : "No Companies"
                      } - ${
                        ownerDetails?.properties?.map(
                          (property) => property.name
                        ) || "No Properties"
                      }`,
                    }
                  : null
              }
              placeholder="Select an owner to preview..."
              onChange={handleOwnerSelect}
              isSearchable
              isClearable
              menuPortalTarget={document.body}
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 100001 }),
                menu: (base) => ({ ...base, zIndex: 100001 }),
              }}
            />
          )}
        </Box>

        {loading ? (
          <Loader />
        ) : ownerDetails ? (
          <Box>
            <GiveTransactionButton width="500px" 
              owner={ownerDetails._id}
            />

            <Card sx={{ width: "100%", mb: 3 }}>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                  <Avatar
                    sx={{
                      bgcolor: getColor(ownerDetails.name),
                      width: 64,
                      height: 64,
                      fontSize: 32,
                      mr: 2,
                    }}
                  >
                    {ownerDetails.name?.charAt(0).toUpperCase()}
                  </Avatar>
                  <Box>
                    <Typography variant="h5" component="h2">
                      {ownerDetails.name}
                    </Typography>
                    <Chip
                      label="Owner"
                      color="primary"
                      size="small"
                      sx={{ mt: 0.5 }}
                    />
                  </Box>
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" color="text.secondary">
                      Username
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {ownerDetails.username}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" color="text.secondary">
                      Email
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {ownerDetails.email}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" color="text.secondary">
                      Phone
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {ownerDetails.phone || "Not provided"}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" color="text.secondary">
                      Tax ID
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {ownerDetails.tax_id || "Not provided"}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" color="text.secondary">
                      AMKA
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {ownerDetails.amka || "Not provided"}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" color="text.secondary">
                      IBAN
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {ownerDetails.iban || "Not provided"}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" color="text.secondary">
                      Balance
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography
                        variant="body1"
                        gutterBottom
                        sx={{
                          color:
                            (parseFloat(ownerDetails.balance) || 0) <= 0
                              ? "green"
                              : "red",
                          fontWeight: "bold",
                        }}
                      >
                        {formatCurrency(parseFloat(ownerDetails.balance) || 0)}
                      </Typography>
                      {user.permissions.canEditBalance && (
                        <Tooltip title="Edit Balance">
                          <IconButton
                            size="small"
                            onClick={handleEditBalance}
                            sx={{ color: "primary.main" }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Companies section */}
            {ownerDetails.companies && ownerDetails.companies.length > 0 && (
              <Card sx={{ width: "100%", mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Associated Companies
                  </Typography>

                  {ownerDetails.companies.length > 0 ? (
                    <Grid container spacing={2}>
                      {ownerDetails.companies.map((company, index) => (
                        <Grid item xs={12} key={index}>
                          <Card variant="outlined" sx={{ mb: 2, p: 2 }}>
                            <Typography variant="subtitle1" fontWeight="bold">
                              {company.name}
                            </Typography>
                            <Grid container spacing={2}>
                              <Grid item xs={12} md={6}>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  Tax ID:
                                </Typography>
                                <Typography variant="body2">
                                  {company.taxId}
                                </Typography>
                              </Grid>

                              {company.iban && (
                                <Grid item xs={12}>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    IBAN:
                                  </Typography>
                                  <Typography variant="body2">
                                    {company.iban}
                                  </Typography>
                                </Grid>
                              )}
                              {company.notes && (
                                <Grid item xs={12}>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    Notes:
                                  </Typography>
                                  <Typography variant="body2">
                                    {company.notes}
                                  </Typography>
                                </Grid>
                              )}
                            </Grid>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <Typography>No company details available</Typography>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Remaining Transactions section */}
            {ownerDetails.remaining_transactions &&
              ownerDetails.remaining_transactions.length > 0 && (
                <Card sx={{ width: "100%", mb: 3 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Remaining Transactions
                    </Typography>

                    <Grid container spacing={2}>
                      {/* Summary Card */}
                      <Grid item xs={12} md={4}>
                        <Card
                          variant="outlined"
                          sx={{ p: 2, backgroundColor: "#f5f5f5" }}
                        >
                          <Typography
                            variant="subtitle1"
                            fontWeight="bold"
                            gutterBottom
                          >
                            Summary
                          </Typography>
                          <Grid container spacing={1}>
                            <Grid item xs={12}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Total Transactions:
                              </Typography>
                              <Typography variant="body1" fontWeight="bold">
                                {ownerDetails.remaining_transactions.length}
                              </Typography>
                            </Grid>
                            <Grid item xs={12}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Total Remaining Amount:
                              </Typography>
                              <Typography
                                variant="h6"
                                fontWeight="bold"
                                sx={{
                                  color:
                                    ownerDetails.remaining_transactions.reduce(
                                      (sum, transaction) =>
                                        sum +
                                        (transaction.remaining_amount || 0),
                                      0
                                    ) < 0
                                      ? "red"
                                      : "green",
                                }}
                              >
                                {formatCurrency(
                                  ownerDetails.remaining_transactions.reduce(
                                    (sum, transaction) =>
                                      sum + (transaction.remaining_amount || 0),
                                    0
                                  )
                                )}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Card>
                      </Grid>

                      {/* Transactions List */}
                      <Grid item xs={12} md={8}>
                        <Typography
                          variant="subtitle1"
                          fontWeight="bold"
                          gutterBottom
                        >
                          Transaction Details
                        </Typography>
                        <TableContainer
                          component={Paper}
                          sx={{ maxHeight: 400 }}
                        >
                          <Table stickyHeader size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Date</TableCell>
                                <TableCell>Amount</TableCell>
                                <TableCell>Remaining</TableCell>
                                <TableCell>Reason</TableCell>
                                <TableCell>Property</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {ownerDetails.remaining_transactions.map(
                                (transaction, index) => (
                                  <TableRow key={transaction._id || index}>
                                    <TableCell>
                                      {new Date(
                                        transaction.created_at ||
                                          transaction.transaction_date
                                      ).toLocaleDateString()}
                                    </TableCell>
                                    <TableCell>
                                      {formatCurrency(transaction.amount)}
                                    </TableCell>
                                    <TableCell>
                                      <Typography
                                        variant="body2"
                                        fontWeight="bold"
                                        sx={{
                                          color:
                                            (transaction.remaining_amount ||
                                              0) < 0
                                              ? "red"
                                              : "green",
                                        }}
                                      >
                                        {formatCurrency(
                                          transaction.remaining_amount || 0
                                        )}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      {transaction.reason?.title || "-"}
                                    </TableCell>
                                    <TableCell>
                                      {transaction.property?.name || "-"}
                                    </TableCell>
                                  </TableRow>
                                )
                              )}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              )}

            {/* Properties section */}
            {ownerDetails.properties && ownerDetails.properties.length > 0 && (
              <Card sx={{ width: "100%" }}>
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 2,
                    }}
                  >
                    <Typography variant="h6">Properties</Typography>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      {/* <AddInvoiceButton
                        propertyId={selectedPropertyForCharge?._id}
                        onInvoiceAdded={handleChargeAdded}
                      /> */}

                      {user.permissions.canDecideTransaction && (
                        <Tooltip title="Create Fund Transaction">
                          <PrimaryButton
                            text="Create Fund Transaction"
                            onClick={() => setFundTransactionPopupOpen(true)}
                          >
                            <NewFundTransactionForm
                              onSuccess={() =>
                                setFundTransactionPopupOpen(false)
                              }
                              partner={ownerDetails}
                            />
                          </PrimaryButton>
                        </Tooltip>
                      )}

                      {user.permissions.canAddCharge && (
                        <AddChargeButton
                          selectedProperty={selectedPropertyForCharge}
                        />
                      )}

                      {user.permissions.canAddInvoice && (
                        <Tooltip title="Create Invoice from Selected Charges">
                          <Button
                            variant="contained"
                            color="secondary"
                            startIcon={<AddIcon />}
                            onClick={handleCreateInvoiceFromMultipleCharges}
                            disabled={
                              selectedCharges.length === 0 ||
                              selectedCharges.some((charge) => charge.invoice)
                            }
                            sx={{ mb: 2 }}
                          >
                            Create Invoice
                          </Button>

                          {/* <IconButton
                          onClick={handleCreateInvoiceFromMultipleCharges}
                          disabled={selectedCharges.length === 0}
                        >
                          <AddIcon />
                        </IconButton> */}
                        </Tooltip>
                      )}
                    </Box>
                  </Box>

                  {ownerDetails.properties.length > 0 ? (
                    <Box sx={{ width: "100%" }}>
                      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                        <Tabs
                          value={tabValue}
                          onChange={handleTabChange}
                          aria-label="property tabs"
                          variant="scrollable"
                          scrollButtons="auto"
                        >
                          <Tab label="All Properties" {...a11yProps(0)} />
                          {ownerDetails.properties.map((property, index) => (
                            <Tab
                              key={property._id}
                              label={property.name}
                              {...a11yProps(index + 1)}
                            />
                          ))}
                        </Tabs>
                      </Box>

                      {/* All Properties Tab */}
                      <TabPanel value={tabValue} index={0}>
                        <Typography variant="h6" gutterBottom>
                          Overview of All Properties
                        </Typography>

                        {/* Replace table with tabs */}
                        <Box sx={{ width: "100%" }}>
                          <Box
                            sx={{
                              borderBottom: 1,
                              borderColor: "divider",
                              mb: 2,
                            }}
                          >
                            <Tabs
                              value={allPropertiesTabValue}
                              onChange={handleAllPropertiesTabChange}
                              aria-label="all properties financial tabs"
                            >
                              <Tab label="Charges" {...financeTabProps(0)} />
                              <Tab label="Invoices" {...financeTabProps(1)} />
                              <Tab
                                label="Transactions"
                                {...financeTabProps(2)}
                              />
                            </Tabs>
                          </Box>

                          {/* Charges Tab for All Properties */}
                          {user.permissions.canViewCharges && (
                            <FinanceTabPanel
                              value={allPropertiesTabValue}
                              index={0}
                            >
                              <Typography variant="subtitle1" gutterBottom>
                                All Charges
                              </Typography>

                              <TableContainer component={Paper}>
                                <Table aria-label="all charges table">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>Select</TableCell>
                                      <TableCell>
                                        <TableSortLabel
                                          active={
                                            allPropertiesChargesSorting.sortField ===
                                            "property"
                                          }
                                          direction={
                                            allPropertiesChargesSorting.sortField ===
                                            "property"
                                              ? allPropertiesChargesSorting.sortOrder ===
                                                1
                                                ? "asc"
                                                : "desc"
                                              : "asc"
                                          }
                                          onClick={() =>
                                            handleAllPropertiesChargesSortChange(
                                              "property"
                                            )
                                          }
                                        >
                                          Property
                                        </TableSortLabel>
                                      </TableCell>
                                      <TableCell>
                                        <TableSortLabel
                                          active={
                                            allPropertiesChargesSorting.sortField ===
                                            "created_at"
                                          }
                                          direction={
                                            allPropertiesChargesSorting.sortField ===
                                            "created_at"
                                              ? allPropertiesChargesSorting.sortOrder ===
                                                1
                                                ? "asc"
                                                : "desc"
                                              : "asc"
                                          }
                                          onClick={() =>
                                            handleAllPropertiesChargesSortChange(
                                              "created_at"
                                            )
                                          }
                                        >
                                          Date
                                        </TableSortLabel>
                                      </TableCell>
                                      <TableCell>
                                        <TableSortLabel
                                          active={
                                            allPropertiesChargesSorting.sortField ===
                                            "amount"
                                          }
                                          direction={
                                            allPropertiesChargesSorting.sortField ===
                                            "amount"
                                              ? allPropertiesChargesSorting.sortOrder ===
                                                1
                                                ? "asc"
                                                : "desc"
                                              : "asc"
                                          }
                                          onClick={() =>
                                            handleAllPropertiesChargesSortChange(
                                              "amount"
                                            )
                                          }
                                        >
                                          Amount
                                        </TableSortLabel>
                                      </TableCell>
                                      <TableCell>
                                        <TableSortLabel
                                          active={
                                            allPropertiesChargesSorting.sortField ===
                                            "remaining_amount"
                                          }
                                          direction={
                                            allPropertiesChargesSorting.sortField ===
                                            "remaining_amount"
                                              ? allPropertiesChargesSorting.sortOrder ===
                                                1
                                                ? "asc"
                                                : "desc"
                                              : "asc"
                                          }
                                          onClick={() =>
                                            handleAllPropertiesChargesSortChange(
                                              "remaining_amount"
                                            )
                                          }
                                        >
                                          Remaining
                                        </TableSortLabel>
                                      </TableCell>
                                      <TableCell>Invoice #</TableCell>
                                      <TableCell>Notes</TableCell>
                                      <TableCell>
                                        <TableSortLabel
                                          active={
                                            allPropertiesChargesSorting.sortField ===
                                            "paid"
                                          }
                                          direction={
                                            allPropertiesChargesSorting.sortField ===
                                            "paid"
                                              ? allPropertiesChargesSorting.sortOrder ===
                                                1
                                                ? "asc"
                                                : "desc"
                                              : "asc"
                                          }
                                          onClick={() =>
                                            handleAllPropertiesChargesSortChange(
                                              "paid"
                                            )
                                          }
                                        >
                                          Status
                                        </TableSortLabel>
                                      </TableCell>
                                      <TableCell>Actions</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {(() => {
                                      const { charges: paginatedCharges } =
                                        getAllPropertiesCharges();

                                      if (ownerChargesLoading) {
                                        return (
                                          <TableRow>
                                            <TableCell colSpan={9}>
                                              <Box
                                                sx={{
                                                  display: "flex",
                                                  justifyContent: "center",
                                                  p: 2,
                                                }}
                                              >
                                                <Loader size={30} />
                                              </Box>
                                            </TableCell>
                                          </TableRow>
                                        );
                                      }

                                      if (paginatedCharges.length === 0) {
                                        return (
                                          <TableRow>
                                            <TableCell
                                              colSpan={9}
                                              align="center"
                                            >
                                              No charges available
                                            </TableCell>
                                          </TableRow>
                                        );
                                      }

                                      return paginatedCharges.map((charge) => (
                                        <TableRow key={charge._id}>
                                          <TableCell>
                                            {!charge.invoice &&
                                              user.permissions.canEditCharge &&
                                              user.permissions
                                                .canAddInvoice && (
                                                <Checkbox
                                                  checked={selectedCharges.some(
                                                    (c) => c._id === charge._id
                                                  )}
                                                  onChange={(e) =>
                                                    handleChargeSelect(
                                                      charge,
                                                      e.target.checked
                                                    )
                                                  }
                                                />
                                              )}
                                          </TableCell>
                                          <TableCell>
                                            {charge.property.name}
                                          </TableCell>
                                          <TableCell>
                                            {new Date(
                                              charge.created_at
                                            ).toLocaleDateString()}
                                          </TableCell>
                                          <TableCell>
                                            {formatCurrency(charge.amount)}
                                          </TableCell>
                                          <TableCell>
                                            {formatCurrency(
                                              charge.remaining_amount
                                            )}
                                          </TableCell>
                                          <TableCell>
                                            {charge.invoice
                                              ? charge.invoice.invoice_number
                                              : "-"}
                                          </TableCell>
                                          <TableCell
                                            style={{
                                              maxWidth: "150px",
                                              overflow: "hidden",
                                              textOverflow: "ellipsis",
                                              whiteSpace: "nowrap",
                                            }}
                                          >
                                            {charge.notes ? (
                                              <Tooltip
                                                title={charge.notes}
                                                PopperProps={{
                                                  style: { zIndex: 10001 },
                                                  modifiers: [
                                                    {
                                                      name: "offset",
                                                      options: {
                                                        offset: [0, -10],
                                                      },
                                                    },
                                                  ],
                                                  sx: {
                                                    "& .MuiTooltip-tooltip": {
                                                      fontSize: "13px",
                                                    },
                                                  },
                                                }}
                                              >
                                                <span>
                                                  {charge.notes.substring(
                                                    0,
                                                    30
                                                  ) +
                                                    (charge.notes.length > 30
                                                      ? "..."
                                                      : "")}
                                                </span>
                                              </Tooltip>
                                            ) : (
                                              "-"
                                            )}
                                          </TableCell>
                                          <TableCell>
                                            <Chip
                                              label={
                                                charge.paid ? "Paid" : "Unpaid"
                                              }
                                              color={
                                                charge.paid
                                                  ? "success"
                                                  : "warning"
                                              }
                                              size="small"
                                            />
                                          </TableCell>
                                          <TableCell>
                                            <Tooltip title="View Details">
                                              <IconButton
                                                onClick={() =>
                                                  handleViewCharge(charge)
                                                }
                                              >
                                                <RemoveRedEyeIcon />
                                              </IconButton>
                                            </Tooltip>
                                            {charge.model &&
                                              charge.ref_of_model && (
                                                <Tooltip
                                                  title={`Preview ${charge.model} Details`}
                                                >
                                                  <IconButton
                                                    color="info"
                                                    onClick={() =>
                                                      handleOpenModelPreview(
                                                        charge
                                                      )
                                                    }
                                                  >
                                                    <PreviewIcon />
                                                  </IconButton>
                                                </Tooltip>
                                              )}
                                            {!charge.invoice && (
                                              <Tooltip title="Create Invoice">
                                                <IconButton
                                                  onClick={() =>
                                                    handleCreateInvoice(charge)
                                                  }
                                                >
                                                  <AddIcon />
                                                </IconButton>
                                              </Tooltip>
                                            )}
                                            {!charge.paid && (
                                              <Tooltip title="Pay Charge">
                                                <IconButton
                                                  color="success"
                                                  onClick={() => {
                                                    setSelectedChargeForPayment(
                                                      charge
                                                    );
                                                    setPaymentError("");
                                                    setPaymentSuccess(false);

                                                    // Initialize transaction allocations
                                                    if (
                                                      ownerDetails?.remaining_transactions
                                                    ) {
                                                      const initialAllocations =
                                                        ownerDetails.remaining_transactions.map(
                                                          (transaction) => ({
                                                            transactionId:
                                                              transaction._id,
                                                            transactionAmount:
                                                              transaction.amount,
                                                            remainingAmount:
                                                              transaction.remaining_amount ||
                                                              transaction.amount,
                                                            allocatedAmount: 0,
                                                            reason:
                                                              transaction.reason
                                                                ?.title ||
                                                              transaction.reason ||
                                                              "Unknown",
                                                            property:
                                                              transaction
                                                                .property
                                                                ?.name ||
                                                              transaction.property ||
                                                              "Unknown",
                                                            date:
                                                              transaction.created_at ||
                                                              transaction.transaction_date,
                                                          })
                                                        );
                                                      setTransactionAllocations(
                                                        initialAllocations
                                                      );
                                                    } else {
                                                      setTransactionAllocations(
                                                        []
                                                      );
                                                    }
                                                    setTotalAllocatedAmount(0);

                                                    setPaymentDialogOpen(true);
                                                  }}
                                                >
                                                  <AttachMoneyIcon />
                                                </IconButton>
                                              </Tooltip>
                                            )}
                                            {user.permissions
                                              ?.canDeleteCharge !== false && (
                                              <Tooltip title="Delete Charge">
                                                <IconButton
                                                  color="error"
                                                  onClick={() =>
                                                    handleDeleteCharge(charge)
                                                  }
                                                >
                                                  <DeleteIcon />
                                                </IconButton>
                                              </Tooltip>
                                            )}
                                          </TableCell>
                                        </TableRow>
                                      ));
                                    })()}
                                  </TableBody>
                                </Table>
                              </TableContainer>

                              {/* Pagination for All Properties Charges */}
                              {(() => {
                                const { totalCharges } =
                                  getAllPropertiesCharges();
                                return (
                                  <TablePagination
                                    component="div"
                                    count={totalCharges}
                                    page={allPropertiesChargesPagination.page}
                                    onPageChange={
                                      handleAllPropertiesChargesPageChange
                                    }
                                    rowsPerPage={
                                      allPropertiesChargesPagination.rowsPerPage
                                    }
                                    onRowsPerPageChange={
                                      handleAllPropertiesChargesRowsPerPageChange
                                    }
                                    rowsPerPageOptions={[5, 10, 25, 50]}
                                  />
                                );
                              })()}
                            </FinanceTabPanel>
                          )}

                          {/* Invoices Tab for All Properties */}
                          {user.permissions.canViewInvoices && (
                            <FinanceTabPanel
                              value={allPropertiesTabValue}
                              index={1}
                            >
                              <Typography variant="subtitle1" gutterBottom>
                                All Invoices
                              </Typography>

                              {/* Pass a valid array of property IDs and ensure it's not empty */}
                              <InvoiceTable
                                propertyIds={
                                  ownerDetails?.properties
                                    ?.map((property) => property._id)
                                    .filter(Boolean) || []
                                }
                                onInvoiceUpdated={() =>
                                  fetchOwnerDetails(selectedOwner)
                                }
                              />
                            </FinanceTabPanel>
                          )}

                          {/* Transactions Tab for All Properties */}
                          {user.permissions.canDecideTransaction && (
                            <FinanceTabPanel
                              value={allPropertiesTabValue}
                              index={2}
                            >
                              <Typography variant="subtitle1" gutterBottom>
                                All Transactions
                              </Typography>

                              <TransactionTable
                                user={ownerDetails._id}
                                isMini
                              />
                            </FinanceTabPanel>
                          )}
                        </Box>
                      </TabPanel>

                      {/* Individual Property Tabs */}
                      {ownerDetails.properties.map((property, index) => (
                        <TabPanel
                          key={property._id}
                          value={tabValue}
                          index={index + 1}
                        >
                          {/* Inner tabs for financial information */}
                          <Box sx={{ width: "100%" }}>
                            <Box
                              sx={{
                                borderBottom: 1,
                                borderColor: "divider",
                                mb: 2,
                              }}
                            >
                              <Tabs
                                value={financeTabValues[property._id] || 0}
                                onChange={(e, newValue) =>
                                  handleFinanceTabChange(property._id, newValue)
                                }
                                aria-label={`${property.name} financial tabs`}
                              >
                                {user.permissions.canViewCharges && (
                                  <Tab
                                    label="Charges"
                                    {...financeTabProps(0)}
                                  />
                                )}
                                {user.permissions.canViewInvoices && (
                                  <Tab
                                    label="Invoices"
                                    {...financeTabProps(1)}
                                  />
                                )}
                                {user.permissions.canDecideTransaction && (
                                  <Tab
                                    label="Transactions"
                                    {...financeTabProps(2)}
                                  />
                                )}
                              </Tabs>
                            </Box>

                            {/* Charges Tab */}
                            {user.permissions.canViewCharges && (
                              <FinanceTabPanel
                                value={financeTabValues[property._id] || 0}
                                index={0}
                              >
                                <Typography variant="h6" gutterBottom>
                                  Charges for {property.name}
                                </Typography>

                                {chargesLoading[property._id] ? (
                                  <Box
                                    sx={{
                                      display: "flex",
                                      justifyContent: "center",
                                      p: 3,
                                    }}
                                  >
                                    <Loader />
                                  </Box>
                                ) : propertyCharges[property._id]?.length >
                                  0 ? (
                                  <>
                                    <TableContainer component={Paper}>
                                      <Table aria-label="charges table">
                                        <TableHead>
                                          <TableRow>
                                            <TableCell>Select</TableCell>
                                            <TableCell>
                                              <TableSortLabel
                                                active={
                                                  (chargesSorting[property._id]
                                                    ?.sortField || "paid") ===
                                                  "created_at"
                                                }
                                                direction={
                                                  chargesSorting[property._id]
                                                    ?.sortField ===
                                                    "created_at" &&
                                                  chargesSorting[property._id]
                                                    ?.sortOrder === 1
                                                    ? "asc"
                                                    : "desc"
                                                }
                                                onClick={() =>
                                                  handleChargesSortChange(
                                                    property._id,
                                                    "created_at"
                                                  )
                                                }
                                              >
                                                Date
                                              </TableSortLabel>
                                            </TableCell>
                                            <TableCell>
                                              <TableSortLabel
                                                active={
                                                  (chargesSorting[property._id]
                                                    ?.sortField || "paid") ===
                                                  "amount"
                                                }
                                                direction={
                                                  chargesSorting[property._id]
                                                    ?.sortField === "amount" &&
                                                  chargesSorting[property._id]
                                                    ?.sortOrder === 1
                                                    ? "asc"
                                                    : "desc"
                                                }
                                                onClick={() =>
                                                  handleChargesSortChange(
                                                    property._id,
                                                    "amount"
                                                  )
                                                }
                                              >
                                                Amount
                                              </TableSortLabel>
                                            </TableCell>
                                            <TableCell>
                                              <TableSortLabel
                                                active={
                                                  (chargesSorting[property._id]
                                                    ?.sortField || "paid") ===
                                                  "remaining_amount"
                                                }
                                                direction={
                                                  chargesSorting[property._id]
                                                    ?.sortField ===
                                                    "remaining_amount" &&
                                                  chargesSorting[property._id]
                                                    ?.sortOrder === 1
                                                    ? "asc"
                                                    : "desc"
                                                }
                                                onClick={() =>
                                                  handleChargesSortChange(
                                                    property._id,
                                                    "remaining_amount"
                                                  )
                                                }
                                              >
                                                Remaining
                                              </TableSortLabel>
                                            </TableCell>
                                            <TableCell>Invoice #</TableCell>
                                            <TableCell>Notes</TableCell>
                                            <TableCell>
                                              <TableSortLabel
                                                active={
                                                  (chargesSorting[property._id]
                                                    ?.sortField || "paid") ===
                                                  "paid"
                                                }
                                                direction={
                                                  chargesSorting[property._id]
                                                    ?.sortField === "paid" &&
                                                  chargesSorting[property._id]
                                                    ?.sortOrder === 1
                                                    ? "asc"
                                                    : "desc"
                                                }
                                                onClick={() =>
                                                  handleChargesSortChange(
                                                    property._id,
                                                    "paid"
                                                  )
                                                }
                                              >
                                                Status
                                              </TableSortLabel>
                                            </TableCell>
                                            <TableCell>Actions</TableCell>
                                          </TableRow>
                                        </TableHead>
                                        <TableBody>
                                          {propertyCharges[property._id].map(
                                            (charge) => (
                                              <TableRow key={charge._id}>
                                                <TableCell>
                                                  <Checkbox
                                                    checked={selectedCharges.some(
                                                      (c) =>
                                                        c._id === charge._id
                                                    )}
                                                    onChange={(e) =>
                                                      handleChargeSelect(
                                                        charge,
                                                        e.target.checked
                                                      )
                                                    }
                                                  />
                                                </TableCell>
                                                <TableCell>
                                                  {new Date(
                                                    charge.created_at
                                                  ).toLocaleDateString()}
                                                </TableCell>
                                                <TableCell>
                                                  {formatCurrency(
                                                    charge.amount
                                                  )}
                                                </TableCell>
                                                <TableCell>
                                                  {formatCurrency(
                                                    charge.remaining_amount
                                                  )}
                                                </TableCell>
                                                <TableCell>
                                                  {charge.invoice
                                                    ? charge.invoice
                                                        .invoice_number
                                                    : "-"}
                                                </TableCell>
                                                <TableCell
                                                  style={{
                                                    maxWidth: "150px",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    whiteSpace: "nowrap",
                                                  }}
                                                >
                                                  {charge.notes ? (
                                                    <Tooltip
                                                      title={charge.notes}
                                                      PopperProps={{
                                                        style: {
                                                          zIndex: 10001,
                                                        },
                                                        modifiers: [
                                                          {
                                                            name: "offset",
                                                            options: {
                                                              offset: [0, -10],
                                                            },
                                                          },
                                                        ],
                                                        sx: {
                                                          "& .MuiTooltip-tooltip":
                                                            {
                                                              fontSize: "13px",
                                                            },
                                                        },
                                                      }}
                                                    >
                                                      <span>
                                                        {charge.notes.substring(
                                                          0,
                                                          30
                                                        ) +
                                                          (charge.notes.length >
                                                          30
                                                            ? "..."
                                                            : "")}
                                                      </span>
                                                    </Tooltip>
                                                  ) : (
                                                    "-"
                                                  )}
                                                </TableCell>
                                                <TableCell>
                                                  <Chip
                                                    label={
                                                      charge.paid
                                                        ? "Paid"
                                                        : "Unpaid"
                                                    }
                                                    color={
                                                      charge.paid
                                                        ? "success"
                                                        : "warning"
                                                    }
                                                    size="small"
                                                  />
                                                </TableCell>
                                                <TableCell>
                                                  <Tooltip title="View Details">
                                                    <IconButton
                                                      onClick={() =>
                                                        handleViewCharge(charge)
                                                      }
                                                    >
                                                      <RemoveRedEyeIcon />
                                                    </IconButton>
                                                  </Tooltip>
                                                  {charge.model &&
                                                    charge.ref_of_model && (
                                                      <Tooltip
                                                        title={`Preview ${charge.model} Details`}
                                                      >
                                                        <IconButton
                                                          color="info"
                                                          onClick={() =>
                                                            handleOpenModelPreview(
                                                              charge
                                                            )
                                                          }
                                                        >
                                                          <PreviewIcon />
                                                        </IconButton>
                                                      </Tooltip>
                                                    )}
                                                  {!charge.invoice && (
                                                    <Tooltip title="Create Invoice">
                                                      <IconButton
                                                        onClick={() =>
                                                          handleCreateInvoice(
                                                            charge
                                                          )
                                                        }
                                                      >
                                                        <AddIcon />
                                                      </IconButton>
                                                    </Tooltip>
                                                  )}
                                                  {!charge.paid && (
                                                    <Tooltip title="Pay Charge">
                                                      <IconButton
                                                        color="success"
                                                        onClick={() => {
                                                          setSelectedChargeForPayment(
                                                            charge
                                                          );
                                                          setPaymentError("");
                                                          setPaymentSuccess(
                                                            false
                                                          );

                                                          // Initialize transaction allocations
                                                          if (
                                                            ownerDetails?.remaining_transactions
                                                          ) {
                                                            const initialAllocations =
                                                              ownerDetails.remaining_transactions.map(
                                                                (
                                                                  transaction
                                                                ) => ({
                                                                  transactionId:
                                                                    transaction._id,
                                                                  transactionAmount:
                                                                    transaction.amount,
                                                                  remainingAmount:
                                                                    transaction.remaining_amount ||
                                                                    transaction.amount,
                                                                  allocatedAmount: 0,
                                                                  reason:
                                                                    transaction
                                                                      .reason
                                                                      ?.title ||
                                                                    transaction.reason ||
                                                                    "Unknown",
                                                                  property:
                                                                    transaction
                                                                      .property
                                                                      ?.name ||
                                                                    transaction.property ||
                                                                    "Unknown",
                                                                  date:
                                                                    transaction.created_at ||
                                                                    transaction.transaction_date,
                                                                })
                                                              );
                                                            setTransactionAllocations(
                                                              initialAllocations
                                                            );
                                                          } else {
                                                            setTransactionAllocations(
                                                              []
                                                            );
                                                          }
                                                          setTotalAllocatedAmount(
                                                            0
                                                          );

                                                          setPaymentDialogOpen(
                                                            true
                                                          );
                                                        }}
                                                      >
                                                        <AttachMoneyIcon />
                                                      </IconButton>
                                                    </Tooltip>
                                                  )}
                                                  {user.permissions
                                                    ?.canDeleteCharge !==
                                                    false && (
                                                    <Tooltip title="Delete Charge">
                                                      <IconButton
                                                        color="error"
                                                        onClick={() =>
                                                          handleDeleteCharge(
                                                            charge
                                                          )
                                                        }
                                                      >
                                                        <DeleteIcon />
                                                      </IconButton>
                                                    </Tooltip>
                                                  )}
                                                </TableCell>
                                              </TableRow>
                                            )
                                          )}
                                        </TableBody>
                                      </Table>
                                    </TableContainer>
                                    {/* Add pagination for individual property charges */}
                                    <TablePagination
                                      rowsPerPageOptions={[5, 10, 25, 50]}
                                      component="div"
                                      count={
                                        chargesTotalItems[property._id] || 0
                                      }
                                      rowsPerPage={
                                        chargesPagination[property._id]
                                          ?.rowsPerPage || 10
                                      }
                                      page={
                                        chargesPagination[property._id]?.page ||
                                        0
                                      }
                                      onPageChange={(event, newPage) =>
                                        handleChargesPageChange(
                                          property._id,
                                          newPage
                                        )
                                      }
                                      onRowsPerPageChange={(event) =>
                                        handleChargesRowsPerPageChange(
                                          property._id,
                                          event
                                        )
                                      }
                                    />
                                  </>
                                ) : (
                                  <Typography>
                                    No charges available for this property.
                                  </Typography>
                                )}
                              </FinanceTabPanel>
                            )}

                            {/* Invoices Tab */}
                            {user.permissions.canViewInvoices && (
                              <FinanceTabPanel
                                value={financeTabValues[property._id] || 0}
                                index={1}
                              >
                                <Typography variant="h6" gutterBottom>
                                  Invoices for {property.name}
                                </Typography>

                                {/* Replace with InvoiceTable component */}
                                <InvoiceTable propertyId={property._id} />
                              </FinanceTabPanel>
                            )}

                            {/* Transactions Tab */}
                            {user.permissions.canDecideTransaction && (
                              <FinanceTabPanel
                                value={financeTabValues[property._id] || 0}
                                index={2}
                              >
                                <Typography variant="h6" gutterBottom>
                                  Transactions for {property.name}
                                </Typography>

                                <TransactionTable
                                  property={property._id}
                                  isMini
                                />
                              </FinanceTabPanel>
                            )}
                          </Box>
                        </TabPanel>
                      ))}
                    </Box>
                  ) : (
                    <Typography>No property details available</Typography>
                  )}
                </CardContent>
              </Card>
            )}
          </Box>
        ) : selectedOwner ? (
          <Typography color="error">
            {error || "Failed to load owner details."}
          </Typography>
        ) : (
          <Typography variant="body1">
            Select an owner to view their details.
          </Typography>
        )}
      </Box>

      {/* Charge View Popup */}
      {chargeViewOpen && (
        <PopUpWindow
          open={chargeViewOpen}
          onClose={handleCloseChargeView}
          unfocusClose={true}
          title="Charge Details"
        >
          <ChargeForm
            selectedProperty={
              selectedCharge?.property._id
                ? selectedPropertyForCharge
                : ownerDetails?.properties.find(
                    (property) => property._id === selectedCharge?.property
                  )
            }
            selectedAmount={selectedCharge?.amount}
            selectedRef={selectedCharge?.reference}
            selectedModel={selectedCharge?.model}
            selectedInvoice={selectedCharge?.invoice}
            selectedAutoGenerated={selectedCharge?.auto_generated}
            selectedNotes={selectedCharge?.notes}
            selectedTransactions={selectedCharge?.transactions}
            readOnly={true}
          />
        </PopUpWindow>
      )}

      {fundTransactionPopupOpen && (
        <PopUpWindow
          onClose={() => setFundTransactionPopupOpen(false)}
          unfocusClose={true}
        >
          <NewFundTransactionForm
            onSuccess={() => setFundTransactionPopupOpen(false)}
            partner={ownerDetails}
          />
        </PopUpWindow>
      )}
      {/* Invoice Form Popup */}
      {invoiceFormOpen && (
        <PopUpWindow
          open={invoiceFormOpen}
          onClose={handleCloseInvoiceForm}
          title="Create Invoice"
        >
          <InvoiceForm
            charge={selectedChargeForInvoice}
            onInvoiceCreated={handleInvoiceCreated}
          />
        </PopUpWindow>
      )}

      {/* Payment Dialog */}
      {paymentDialogOpen && (
        <PopUpWindow
          title={paymentSuccess ? "Payment Successful" : "Pay Charge"}
          onClose={() => {
            if (!processingPayment) {
              setPaymentDialogOpen(false);
              setSelectedChargeForPayment(null);
            }
          }}
          width="600px"
        >
          {paymentSuccess ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                my: 2,
              }}
            >
              <CheckCircleIcon color="success" sx={{ fontSize: 60, mb: 2 }} />
              <Typography variant="h6" align="center">
                Payment successful!
              </Typography>
              <Typography color="text.secondary" align="center">
                The charge has been updated.
              </Typography>
            </Box>
          ) : (
            <>
              {selectedChargeForPayment && (
                <Box sx={{ my: 2 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="subtitle1">
                        Property:{" "}
                        {typeof selectedChargeForPayment.property === "object"
                          ? selectedChargeForPayment.property.name
                          : "Property ID: " + selectedChargeForPayment.property}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="subtitle1">
                        Total Amount:{" "}
                        {formatCurrency(selectedChargeForPayment.amount)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="subtitle1">
                        Remaining Amount:{" "}
                        {formatCurrency(
                          selectedChargeForPayment.remaining_amount
                        )}
                      </Typography>
                    </Grid>
                    {selectedChargeForPayment.invoice && (
                      <Grid item xs={12}>
                        <Typography variant="subtitle1">
                          Invoice:{" "}
                          {typeof selectedChargeForPayment.invoice === "object"
                            ? selectedChargeForPayment.invoice.invoice_number
                            : "Invoice ID: " + selectedChargeForPayment.invoice}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>

                  <TextField
                    margin="normal"
                    label="Payment Amount"
                    type="number"
                    fullWidth
                    value={totalAllocatedAmount}
                    InputProps={{
                      readOnly: true,
                    }}
                    sx={{ mt: 3, mb: 2 }}
                    helperText={`Total Payment: ${formatCurrency(
                      totalAllocatedAmount
                    )} / Max: ${formatCurrency(
                      selectedChargeForPayment.remaining_amount
                    )}`}
                  />

                  {transactionAllocations.length > 0 ? (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="h6" sx={{ mb: 2 }}>
                        Allocate Payment to Transactions
                      </Typography>
                      <Box sx={{ maxHeight: 400, overflowY: "auto" }}>
                        {transactionAllocations.map((transaction, index) => (
                          <Box
                            key={transaction.transactionId}
                            sx={{
                              border: "1px solid #e0e0e0",
                              borderRadius: 1,
                              p: 2,
                              mb: 2,
                              backgroundColor: "#f9f9f9",
                            }}
                          >
                            <Grid container spacing={2} alignItems="center">
                              <Grid item xs={12} sm={3}>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  {new Date(
                                    transaction.date
                                  ).toLocaleDateString()}
                                </Typography>
                                <Typography variant="body2" fontWeight="bold">
                                  {transaction.reason}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {transaction.property}
                                </Typography>
                              </Grid>
                              <Grid item xs={12} sm={3}>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  Remaining:{" "}
                                  {formatCurrency(transaction.remainingAmount)}
                                </Typography>
                              </Grid>
                              <Grid item xs={12} sm={4}>
                                <TextField
                                  label="Allocation Amount"
                                  type="number"
                                  size="small"
                                  fullWidth
                                  value={transaction.allocatedAmount}
                                  onChange={(e) => {
                                    const value =
                                      parseFloat(e.target.value) || 0;
                                    const maxAmount = Math.min(
                                      transaction.remainingAmount,
                                      selectedChargeForPayment.remaining_amount -
                                        (totalAllocatedAmount -
                                          transaction.allocatedAmount)
                                    );

                                    if (value <= maxAmount) {
                                      const newAllocations = [
                                        ...transactionAllocations,
                                      ];
                                      newAllocations[index].allocatedAmount =
                                        value;
                                      setTransactionAllocations(newAllocations);

                                      const newTotal = newAllocations.reduce(
                                        (sum, t) => sum + t.allocatedAmount,
                                        0
                                      );
                                      setTotalAllocatedAmount(newTotal);
                                      setPaymentError("");
                                    }
                                  }}
                                  inputProps={{
                                    min: 0,
                                    max: Math.min(
                                      transaction.remainingAmount,
                                      selectedChargeForPayment.remaining_amount -
                                        (totalAllocatedAmount -
                                          transaction.allocatedAmount)
                                    ),
                                    step: "0.01",
                                  }}
                                  disabled={processingPayment}
                                />
                              </Grid>
                              <Grid item xs={12} sm={2}>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => {
                                    const maxAmount = Math.min(
                                      transaction.remainingAmount,
                                      selectedChargeForPayment.remaining_amount -
                                        (totalAllocatedAmount -
                                          transaction.allocatedAmount)
                                    );

                                    const newAllocations = [
                                      ...transactionAllocations,
                                    ];
                                    newAllocations[index].allocatedAmount =
                                      maxAmount;
                                    setTransactionAllocations(newAllocations);

                                    const newTotal = newAllocations.reduce(
                                      (sum, t) => sum + t.allocatedAmount,
                                      0
                                    );
                                    setTotalAllocatedAmount(newTotal);
                                    setPaymentError("");
                                  }}
                                  disabled={processingPayment}
                                >
                                  Max
                                </Button>
                              </Grid>
                            </Grid>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  ) : (
                    <Box sx={{ mt: 2 }}>
                      <Alert severity="info">
                        No remaining transactions found for this owner. You can
                        still make a payment, but it won't be allocated to
                        specific transactions.
                      </Alert>
                      <TextField
                        margin="normal"
                        label="Payment Amount"
                        type="number"
                        fullWidth
                        value={totalAllocatedAmount}
                        onChange={(e) => {
                          const value = parseFloat(e.target.value) || 0;
                          if (
                            value <= selectedChargeForPayment.remaining_amount
                          ) {
                            setTotalAllocatedAmount(value);
                            setPaymentError("");
                          }
                        }}
                        inputProps={{
                          min: 0,
                          max: selectedChargeForPayment.remaining_amount,
                          step: "0.01",
                        }}
                        disabled={processingPayment}
                        sx={{ mt: 2 }}
                        helperText={`Maximum: ${formatCurrency(
                          selectedChargeForPayment.remaining_amount
                        )}`}
                      />
                    </Box>
                  )}

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={
                          parseFloat(totalAllocatedAmount) ===
                          parseFloat(selectedChargeForPayment.remaining_amount)
                        }
                        onChange={(e) => {
                          if (e.target.checked) {
                            if (transactionAllocations.length > 0) {
                              // Allocate the full remaining charge amount across transactions
                              let remainingToAllocate =
                                selectedChargeForPayment.remaining_amount;
                              const newAllocations = transactionAllocations.map(
                                (transaction) => {
                                  const maxForThisTransaction = Math.min(
                                    transaction.remainingAmount,
                                    remainingToAllocate
                                  );
                                  remainingToAllocate -= maxForThisTransaction;
                                  return {
                                    ...transaction,
                                    allocatedAmount: maxForThisTransaction,
                                  };
                                }
                              );

                              setTransactionAllocations(newAllocations);
                              const newTotal = newAllocations.reduce(
                                (sum, t) => sum + t.allocatedAmount,
                                0
                              );
                              setTotalAllocatedAmount(newTotal);
                            } else {
                              // Simple payment without transaction allocation
                              setTotalAllocatedAmount(
                                selectedChargeForPayment.remaining_amount
                              );
                            }
                            setPaymentError("");
                          } else {
                            if (transactionAllocations.length > 0) {
                              // Clear all allocations
                              const newAllocations = transactionAllocations.map(
                                (transaction) => ({
                                  ...transaction,
                                  allocatedAmount: 0,
                                })
                              );
                              setTransactionAllocations(newAllocations);
                            }
                            setTotalAllocatedAmount(0);
                          }
                        }}
                        disabled={processingPayment}
                      />
                    }
                    label="Pay full remaining amount"
                  />
                </Box>
              )}
              {paymentError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {paymentError}
                </Alert>
              )}
            </>
          )}

          {/* Action Buttons */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
              mt: 3,
              pt: 2,
              borderTop: "1px solid #e0e0e0",
            }}
          >
            {paymentSuccess ? (
              <Button
                onClick={() => {
                  setPaymentDialogOpen(false);
                  setSelectedChargeForPayment(null);
                  setPaymentSuccess(false);
                  // Refresh owner details to show updated balance and charge status
                  // fetchOwnerDetails(selectedOwner);
                }}
                variant="contained"
              >
                Close
              </Button>
            ) : (
              <>
                <Button
                  onClick={() => {
                    if (!processingPayment) {
                      setPaymentDialogOpen(false);
                      setSelectedChargeForPayment(null);
                    }
                  }}
                  disabled={processingPayment}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handlePaymentSubmission}
                  disabled={
                    processingPayment ||
                    !totalAllocatedAmount ||
                    totalAllocatedAmount <= 0
                  }
                  startIcon={
                    processingPayment ? <CircularProgress size={20} /> : null
                  }
                >
                  {processingPayment ? "Processing..." : "Pay Now"}
                </Button>
              </>
            )}
          </Box>
        </PopUpWindow>
      )}

      {/* Edit Balance Dialog */}
      {editBalanceDialogOpen && (
        <PopUpWindow
          title="Edit Owner Balance"
          onClose={handleCloseEditBalance}
          unfocusClose={true}
          width="500px"
        >
          <Typography variant="body1" sx={{ mb: 2 }}>
            Update the balance for {ownerDetails?.name}. Current balance:{" "}
            {formatCurrency(parseFloat(ownerDetails?.balance) || 0)}
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="New Balance (€)"
            type="number"
            fullWidth
            variant="outlined"
            value={newBalance}
            onChange={(e) => setNewBalance(e.target.value)}
            disabled={balanceLoading}
            inputProps={{
              step: "0.01",
            }}
            sx={{ mt: 2 }}
          />
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}

          {/* Action Buttons */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
              mt: 3,
              pt: 2,
              borderTop: "1px solid #e0e0e0",
            }}
          >
            <Button onClick={handleCloseEditBalance} disabled={balanceLoading}>
              Cancel
            </Button>
            <Button
              onClick={handleUpdateBalance}
              variant="contained"
              disabled={balanceLoading}
            >
              {balanceLoading ? (
                <CircularProgress size={20} />
              ) : (
                "Update Balance"
              )}
            </Button>
          </Box>
        </PopUpWindow>
      )}

      {/* Charge Model Preview Dialog */}
      <ChargeModelPreview
        open={modelPreviewOpen}
        onClose={handleCloseModelPreview}
        charge={selectedChargeForPreview}
        title={`Preview ${selectedChargeForPreview?.model || "Model"} Details`}
      />

      {/* ChargeDeleteDialog */}
      <ChargeDeleteDialog
        open={chargeDeleteDialogOpen}
        onClose={handleCloseChargeDeleteDialog}
        charge={chargeToDelete}
        ownerDetails={ownerDetails}
        onDeleteSuccess={handleDeleteSuccess}
      />
    </PageStructure>
  );
};

export default PreviewOwner;
